<template>
	<div class="layout-dashboard">
    <DashboardFilterBar
      :value="bar"
      :savedView="savedView"
      :canSave="isOwnDashboard"
      :restriction="restriction"
      @input="onBarChange"
      @saved-view="onSavedView"
      @widgets-changed="onWidgetsChanged"
    />
    <ProgressSpinner 
      v-if="userWidgetsLoading"
      style="width:25px; height:25px; width: 100%;" 
      strokeWidth="5" 
      fill="#EEEEEE" 
      animationDuration=".5s"
    />
    <div v-else>
      <!-- One ordered list, in the backend's own order (contract §2), each tile as wide as its
           colSpan says. Sortable.js under vuedraggable, so a tablet can rearrange by touch; only
           the handle starts a drag, so a click on the widget itself still opens its report. -->
      <draggable
        :value="tiles"
        class="p-grid"
        :disabled="!canArrange"
        handle=".dashboard-tile-handle"
        @input="onTilesReordered"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <div
          v-for="tile in tiles"
          :key="tile.id"
          :class="tileClass(tile)"
        >
          <!-- Your own dashboard only: the endpoints take no user id, so an admin looking at
               someone else's cannot rearrange it. -->
          <div v-if="canArrange" class="dashboard-tile-controls">
            <i
              class="dashboard-tile-handle pi pi-bars"
              title="Drag to move this widget"
            ></i>
            <Button
              class="p-button-text p-button-sm dashboard-tile-narrower"
              icon="pi pi-minus"
              title="Make this widget narrower"
              :disabled="colSpanFor(tile) <= 1"
              @click="resizeTile(tile, -1)"
            />
            <span class="dashboard-tile-width">{{ widthLabel(tile) }}</span>
            <Button
              class="p-button-text p-button-sm dashboard-tile-wider"
              icon="pi pi-plus"
              title="Make this widget wider"
              :disabled="colSpanFor(tile) >= 4"
              @click="resizeTile(tile, 1)"
            />
          </div>

          <!-- A drop ends with a click on whatever was under the pointer; that click must not
               open the widget's report. -->
          <div class="dashboard-tile-body" @click.capture="guardDrillClick">
            <div
              v-if="isWidgetType(tile) && tileDataFor(tile)"
            >
              <WidgetPos11121314
                :widgetData="tileDataFor(tile)"
                :styleClass="tileStyleClass(tile)"
                :datesSet="barDatesSet"
              />
            </div>
            <div
              v-else-if="isGraphType(tile)"
              :style="{ margin: '0 auto', 'max-width': '160vmin' }"
            >
              <WidgetPos21 
                :widget="tile"
                :filterBar="bar"
                :drill="drillFor(tile)"
              />
            </div>
            <div
              v-else-if="isListType(tile)"
            >
              <WidgetTypeTable 
                :widget="tile"
                :filterBar="bar"
                :drill="drillFor(tile)"
              />
            </div>
          </div>
        </div>
      </draggable>

    </div>  
  </div>
</template>


<script>
import UserService from "@/service/UserService";
import draggable from 'vuedraggable'

import DashboardFilterBar from './DashboardFilterBar.vue'
import WidgetPos11121314 from './WidgetPos_11_12_13_14.vue'
import WidgetPos21 from './WidgetPos_21.vue'
import WidgetTypeTable from './WidgetType_Table.vue'
import {
  applyRestriction,
  branchRestriction,
  emptyBar,
  barFromQuery,
  barFromSavedView,
  barToParams,
  hasDate,
  isEmptyBar,
  mergeBarIntoQuery,
  queryHasBar,
  sameBar,
  widgetPermissionCodes
} from './dashboardFilterBar'

// The filter bar's state (item 4, decision 2b) lives in two places:
//   - the route query, so a reload, Back and a shared link restore it;
//   - Vuex (dashboard/bar), so the menu - which opens the dashboard with no query - does too.
// Where the dashboard opens from, in order: the query; this session's bar; the user's saved
// view; the widgets' own settings. The bar is written back with replace(), never push(), so
// Back does not walk through every filter change.

export default {
  components: {
    draggable,
    DashboardFilterBar,
    WidgetPos11121314,
    WidgetPos21,
    WidgetTypeTable
  },
  created() {
    if(this.$route.query && this.$route.query.toast && this.$route.query.toast === 'yes')
    {
      this.$toast.add({
        severity: "error",
        summary: "Error",
        detail: `No default branch set. Please notify the administrator to allow you to access this report's data.`,
        life: 5000,
      })
    }
    this.setupcomponent()
  },
  data: () => ({
    userService: new UserService(),
    userWidgets: [],
    userWidgetsLoading: false,
    first4WidgetsData: [],
    first4WidgetsDataLoading: false,
    // The one ordered list the dashboard draws: the user's *active* widgets, in the backend's
    // own order (contract §2). The admin screen still sees the inactive rows; the dashboard
    // does not (contract §8).
    tiles: [],
    // True from the moment a drag starts until just after the drop, so the click the drop
    // leaves behind does not open a report.
    dragging: false,
    selectedRow: [],
    userId: null,
    bar: emptyBar(),
    barResolved: false,
    widgetDataSeq: 0,
    // What each widget opens when clicked, from the backend, for the bar as it stands. Tiles
    // carry their own inline; list and graph responses are bare arrays with nowhere to put it.
    widgetDrills: [],
    widgetDrillsSeq: 0
  }),
  computed: {
    profile() {
      return this.$store.getters['auth/getMyProfile']
    },
    // Vuex and the saved view belong to the signed-in user, so they are only used on their own
    // dashboard - not when an admin opens someone else's under /users/:userid/dashboard.
    isOwnDashboard() {
      const routeUser = this.$route.params && this.$route.params.userid
      return !!(this.profile && this.profile.id && (!routeUser || routeUser == this.profile.id))
    },
    savedView() {
      const stored = this.$store.getters['dashboard/savedView']
      return this.isOwnDashboard && stored && stored.userId == this.profile.id ? stored.view : null
    },
    barDatesSet() {
      return hasDate(this.bar)
    },
    // Rearranging is only ever your own dashboard: v1/myDashboardLayout takes no user id, so an
    // admin looking at someone else's under /users/:userid/dashboard gets no controls.
    canArrange() {
      return this.isOwnDashboard
    },
    // W9 §2.2. Reports.vue:945-960's rule, worked out per user: if any permission this user
    // holds at level 5 ("Restricted") is behind a report one of their own widgets opens, their
    // Branch control is fixed to their own branch. Only on their own dashboard - an admin
    // looking at someone else's is not being limited by that person's role.
    restriction() {
      if (!this.isOwnDashboard) return null
      const rows = (this.widgetDrills || []).concat(this.first4WidgetsData || [])
      return branchRestriction({
        permissions: this.$store.getters['auth/getUserPermissions'] || [],
        codes: widgetPermissionCodes(rows, this.$store.getters['reports/getAllReports'] || []),
        profile: this.profile
      })
    }
  },
  watch: {
    // The widgets - and so the reports behind them - arrive after the bar has been settled, so
    // a restriction that only becomes known then is applied when it does.
    restriction(val) {
      if (!val || !this.barResolved) return
      const next = applyRestriction(this.bar, val)
      if (!sameBar(next, this.bar)) this.onBarChange(next)
    },
    profile(val) {
      if (val && val.id) {
        this.setupcomponent()
      }
    },
    // The dashboard stays mounted when its own query changes: a shared link pasted in, or the
    // menu opening "/" with no query while the dashboard is already showing.
    '$route.query'(query) {
      if (!this.barResolved) return
      if (queryHasBar(query)) {
        const next = barFromQuery(query)
        if (!sameBar(next, this.bar)) this.onBarChange(next)
      } else {
        this.rememberBar(this.bar)
      }
    }
  },
  methods: {
    async setupcomponent() {
      if (this.$route.params.userid) {
        this.userId = this.$route.params.userid
      } else if (this.profile && this.profile.id) {
        this.userId = this.profile.id
      }

      // The bar is settled before any widget renders, so list and graph widgets fetch once.
      if (this.userId && !this.barResolved) {
        this.barResolved = true
        this.bar = applyRestriction(await this.initialBar(), this.restriction)
        this.rememberBar(this.bar)
      }
      this.getUserWidgets()
      this.getUserWidgetData()
      this.getUserWidgetDrills()
    },
    async initialBar() {
      const query = this.$route.query || {}
      if (!this.isOwnDashboard) return barFromQuery(query)

      // Always known before the URL is written: it decides whether ?view=widgets is needed.
      const view = await this.loadSavedView()
      if (queryHasBar(query)) return barFromQuery(query)

      const stored = this.$store.getters['dashboard/bar']
      if (stored && stored.userId == this.profile.id && stored.bar) return barFromQuery(stored.bar)

      return barFromSavedView(view) || emptyBar()
    },
    // Fetched once per session per user; the bar uses it for the "My saved view" indicator.
    async loadSavedView() {
      if (!this.isOwnDashboard) return null
      if (this.savedView) return this.savedView
      try {
        const view = await this.userService.getDashboardSavedView()
        this.$store.dispatch('dashboard/setSavedView', { userId: this.profile.id, view: view || { saved: false } })
        return view
      } catch (e) {
        return null
      }
    },
    onSavedView(view) {
      if (!this.isOwnDashboard) return
      this.$store.dispatch('dashboard/setSavedView', { userId: this.profile.id, view: view || { saved: false } })
      this.rememberBar(this.bar)
    },
    onBarChange(next) {
      // A restricted user's own branch stands whatever the bar, the query or a shared link
      // asked for (W9 §2.2), so a link cannot widen what they see.
      this.bar = applyRestriction(barFromQuery(next), this.restriction)
      this.rememberBar(this.bar)
      this.getUserWidgetData()
      this.getUserWidgetDrills()
    },
    // A widget switched on or off in the panel's list. MyWidgets has already emptied the
    // `commons/userWidgets` cache, so this refetch reaches the server and the dashboard shows
    // the change without a page reload (W5c §5.2).
    onWidgetsChanged() {
      this.getUserWidgets()
      this.getUserWidgetData()
      this.getUserWidgetDrills()
    },
    rememberBar(bar) {
      if (this.isOwnDashboard) {
        this.$store.dispatch('dashboard/setBar', { userId: this.profile.id, bar })
      }

      const query = this.$route.query || {}
      // With nothing set and no saved view to tell apart from, the URL stays clean; otherwise
      // an explicit "widget settings" is recorded (?view=widgets) so a reload keeps it.
      let next = mergeBarIntoQuery(query, bar)
      if (isEmptyBar(bar) && !(this.savedView && this.savedView.saved)) {
        delete next.view
      }
      if (JSON.stringify(next) === JSON.stringify(query)) return
      this.$router.replace({ query: next }).catch(() => {})
    },
    // --- rearranging (contract §4, design decision 9) ------------------------------------
    onDragStart() {
      this.dragging = true
    },
    onDragEnd() {
      // A drop ends with a click on whatever was under the pointer. That click arrives after
      // this handler, so the guard is lifted only once it has been and gone.
      setTimeout(() => { this.dragging = false }, 0)
    },
    // Swallows the click a drop leaves behind, so a drag never opens the widget's report.
    guardDrillClick(event) {
      if (!this.dragging || !event) return
      if (event.stopPropagation) event.stopPropagation()
      if (event.preventDefault) event.preventDefault()
    },
    // vuedraggable hands back the whole list, reordered.
    onTilesReordered(next) {
      if (!Array.isArray(next)) return
      this.saveLayout(next)
    },
    // Quarter -> half -> three quarters -> full width, and back. Nothing else on the dashboard
    // moves: only this tile's width changes, and the one PUT carries every row as it now is.
    resizeTile(tile, step) {
      const span = this.colSpanFor(tile) + step
      if (span < 1 || span > 4) return
      this.saveLayout(this.tiles.map(t => (t === tile ? Object.assign({}, t, { colSpan: span }) : t)))
    },
    // One call for the whole layout (contract §4): displayOrder renumbered 0-based and
    // contiguous over what is on the screen, colSpan as it is drawn - so the stored rows are
    // exactly what the next reload draws, which is the acceptance. The screen shows the new
    // arrangement at once and is put back if the server refuses it; it is never left showing
    // an order that was not stored.
    saveLayout(next) {
      const previous = this.tiles
      this.tiles = next

      const layout = next.map((tile, index) => ({
        id: tile.id,
        displayOrder: index,
        colSpan: this.colSpanFor(tile)
      }))

      this.userService
        .saveMyDashboardLayout(layout)
        .then(saved => {
          const rows = Array.isArray(saved) && saved.length ? saved : this.widgetsWithLayout(layout)
          this.userWidgets = rows
          // getUserWidgets serves this back from Vuex for the rest of the session, so the
          // cached copy has to carry the layout too or re-opening the dashboard undoes the drag.
          this.$store.dispatch('commons/userWidgets', rows)
          this.setupTiles()
        })
        .catch(() => {
          this.tiles = previous
          this.$store.dispatch('addErrorMessage', {
            severity: 'error',
            content: 'Your dashboard layout was not saved from "myDashboardLayout"'
          })
        })
    },
    // The user's rows with the layout just stored written onto them; the inactive ones, which
    // the dashboard does not draw but the admin screen does, are left exactly as they are.
    widgetsWithLayout(layout) {
      const by = {}
      layout.forEach(row => { by[row.id] = row })
      return this.userWidgets.map(widget => {
        const row = by[widget.id]
        return row
          ? Object.assign({}, widget, { displayOrder: row.displayOrder, colSpan: row.colSpan })
          : widget
      })
    },
    // --- the ordered list -------------------------------------------------------------
    // The backend's own order, so the two halves agree:
    //   ORDER BY COALESCE(displayOrder, 999), screenPos, id     (contract §2)
    // Before the migration every displayOrder is null, so this is exactly today's screen_pos
    // order and the dashboard renders identically.
    orderedActiveWidgets(widgets) {
      return (widgets || [])
        .filter(w => w && w.active)
        .slice()
        .sort((a, b) => {
          const ao = a.displayOrder === null || a.displayOrder === undefined ? 999 : Number(a.displayOrder)
          const bo = b.displayOrder === null || b.displayOrder === undefined ? 999 : Number(b.displayOrder)
          if (ao !== bo) return ao - bo
          const as = a.screenPos === null || a.screenPos === undefined ? '' : String(a.screenPos)
          const bs = b.screenPos === null || b.screenPos === undefined ? '' : String(b.screenPos)
          if (as !== bs) return as < bs ? -1 : 1
          return (Number(a.id) || 0) - (Number(b.id) || 0)
        })
    },
    setupTiles() {
      this.tiles = this.orderedActiveWidgets(this.userWidgets)
    },
    // --- width ------------------------------------------------------------------------
    // Quarters. Null until the migration runs, and then it is read from the row.
    colSpanFor(tile) {
      const n = Number(tile && tile.colSpan)
      if (Number.isInteger(n) && n >= 1 && n <= 4) return n
      return this.colSpanFromScreenPos(tile)
    },
    // The same rule script 18 uses (contract §6): how many distinct columns this user has in
    // that screen_pos row - 1 across is full width, 2 are halves, 3 or more are quarters. A
    // junk, single-character or missing code renders alone on its own row, as it does today.
    colSpanFromScreenPos(tile) {
      const pos = tile && tile.screenPos !== null && tile.screenPos !== undefined ? String(tile.screenPos) : ''
      if (!/^[1-9][1-9]$/.test(pos)) return 4

      const columns = {}
      this.tiles.forEach(other => {
        const p = other && other.screenPos !== null && other.screenPos !== undefined ? String(other.screenPos) : ''
        if (/^[1-9][1-9]$/.test(p) && p[0] === pos[0]) columns[p[1]] = true
      })
      const across = Object.keys(columns).length
      if (across <= 1) return 4
      if (across === 2) return 2
      return 1
    },
    // The PrimeVue column, keeping the responsive behaviour the grid has today: everything
    // stacks on a phone (p-col-12) and only widens from p-lg up.
    widthClass(span) {
      if (span === 1) return 'p-col-12 p-xs-12 p-lg-6 p-xl-3'
      if (span === 2) return 'p-col-12 p-xs-6 p-lg-6 p-xl-6'
      if (span === 3) return 'p-col-12 p-xs-12 p-lg-9 p-xl-9'
      return 'p-col-12 p-xs-12 p-lg-12 p-xl-12'
    },
    tileClass(tile) {
      return 'dashboard-tile-col ' + this.widthClass(this.colSpanFor(tile))
    },
    widthLabel(tile) {
      const span = this.colSpanFor(tile)
      return span === 1 ? 'Quarter' : span === 2 ? 'Half' : span === 3 ? 'Three quarters' : 'Full width'
    },
    // --- what a tile is ---------------------------------------------------------------
    isWidgetType(tile) {
      return !!(tile && tile.widget && tile.widget.widgetType === 'WIDGET')
    },
    isGraphType(tile) {
      return !!(tile && tile.widget && tile.widget.widgetType === 'GRAPH')
    },
    isListType(tile) {
      return !!(tile && tile.widget && tile.widget.widgetType === 'LIST')
    },
    // The four coloured tile styles, by the tile's place among the tiles - which is what the
    // old widgetTypeStlye(col) amounted to for the standard 11/12/13/14 row.
    tileStyleClass(tile) {
      const order = this.tiles.filter(t => this.isWidgetType(t))
      const at = order.indexOf(tile)
      return ['sales', 'views', 'users', 'checkin'][at % 4] || 'sales'
    },
    // --- matching a widget to its figure and its link ----------------------------------
    // Contract §3b: on user_widget.id, which is unambiguous and never moves, falling back to
    // today's widgetPos-then-name so the new bundle still works against the deployed jar.
    screenPosNumber(tile) {
      const n = parseInt(tile && tile.screenPos, 10)
      return Number.isFinite(n) ? n : null
    },
    matchToWidget(rows, tile) {
      if (!tile || !Array.isArray(rows)) return null

      const byId = rows.find(r => r && r.userWidgetId !== null && r.userWidgetId !== undefined && r.userWidgetId === tile.id)
      if (byId) return byId

      const pos = this.screenPosNumber(tile)
      if (pos !== null) {
        const byPos = rows.find(r => r && r.widgetPos === pos)
        if (byPos) return byPos
      }

      const name = tile.widget ? tile.widget.widgetName : null
      return name ? rows.find(r => r && r.widgetName === name) || null : null
    },
    tileDataFor(tile) {
      return this.matchToWidget(this.first4WidgetsData, tile)
    },
    drillFor(tile) {
      return this.matchToWidget(this.widgetDrills, tile)
    },
    getUserWidgets() {
      if (this.userId) {
        this.userWidgetsLoading = true
        this.userWidgets = []

        if (this.profile && this.userId == this.profile.id) {
          this.userService
            .getUserWidgets(this.userId)
            .then(data => {
              

              this.userWidgetsLoading = false
              this.userWidgets = data

              this.setupTiles()
            })
            .catch(error => {
              this.userWidgetsLoading = false
              
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "getUserWidgets/{userId}"'
              })
            })
        } else {
          this.userService
            .getUserWidgetsByUserId(this.userId)
            .then(data => {
              

              this.userWidgetsLoading = false
              this.userWidgets = data

              this.setupTiles()
            })
            .catch(error => {
              this.userWidgetsLoading = false
              
              this.$store.dispatch('addErrorMessage', {
                severity: 'error',
                content: 'Some data was not loaded from "getUserWidgets/{userId}"'
              })
            })
        }
      }
    },
    getUserWidgetDrills() {
      if (!this.userId) return
      // A later bar change supersedes a slower earlier request, as for the tile data.
      const seq = ++this.widgetDrillsSeq

      this.userService
        .getUserWidgetDrills(this.userId, barToParams(this.bar))
        .then(data => {
          if (seq !== this.widgetDrillsSeq) return
          this.widgetDrills = Array.isArray(data) ? data : []
        })
        .catch(error => {
          if (seq !== this.widgetDrillsSeq) return
          // A dashboard that cannot fetch its links is still a working dashboard: the widgets
          // simply are not clickable, so this is not worth an error message to the user.
          this.widgetDrills = []
        })
    },
    getUserWidgetData() {
      if (this.userId) {
        this.first4WidgetsDataLoading = true
        this.first4WidgetsData = []
        // A later bar change supersedes a slower earlier request.
        const seq = ++this.widgetDataSeq
        
        this.userService
          .getUserWidgetData(this.userId, barToParams(this.bar))
          .then(data => {
            if (seq !== this.widgetDataSeq) return
            
            this.first4WidgetsDataLoading = false
            this.first4WidgetsData = data

            // this.first4WidgetsData = new Array(data.lengeth)
            // try {
            //   data.forEach(a => {
            //     this.first4WidgetsData[a.widgetPos - 1] = a
            //   })
            // } catch(e) {
            //   this.first4WidgetsData = data
            // }
          })
          .catch(error => {
            if (seq !== this.widgetDataSeq) return
            this.first4WidgetsDataLoading = false
            
            this.$store.dispatch('addErrorMessage', {
              severity: 'error',
              content: 'Some data was not loaded from "getUserWidgetData/{userId}"'
            })
          })
      }
    }
  }
}
</script>

<style>
/* The per-tile controls: a drag handle and a width stepper, small and out of the way. They are
   only ever drawn on your own dashboard. */
.dashboard-tile-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0.35;
  transition: opacity 0.2s;
}
.dashboard-tile-col:hover .dashboard-tile-controls,
.dashboard-tile-controls:focus-within {
  opacity: 1;
}
.dashboard-tile-handle {
  cursor: move;
  /* Sortable needs a target a finger can find on a tablet. */
  padding: 0.5rem;
  touch-action: none;
}
.dashboard-tile-width {
  font-size: 0.75rem;
  white-space: nowrap;
}
.sortable-ghost {
  opacity: 0.4;
}
/* Tiles in the same row match the tallest of them.
   Before W4 every tile rendered an always-present (usually empty) sub-line, so they happened to
   come out the same height. W4 removes that line from a widget that cannot compare and adds a
   real one to a widget that can, so without this the four tiles sit ragged. Flexbox already
   stretches the columns of a row; it is the coloured box inside that has to fill its column. */
.layout-dashboard .dashboard-tile-col {
  display: flex;
  flex-direction: column;
}
.layout-dashboard .dashboard-tile-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
/* W9 §1: a flex child's default is to shrink to its content, which is how the graph ended up at
   Chart.js's 300 x 150 default - PrimeVue's `.p-chart` has no size of its own, so there was
   nothing for it to fill. This makes every tile body fill the column's width; the graph's own
   height comes from `.dashboard-graph` in WidgetPos_21.vue. */
.layout-dashboard .dashboard-tile-body > div {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}
.layout-dashboard .dashboard-tile-body .overview-box {
  height: 100%;
}
</style>